import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ArrowRight } from 'lucide-react';
import { usePhrases } from '@/context/PhrasesContext';
import { useLanguage } from '@/context/LanguageContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useAuth } from '@/context/AuthContext';
import { getCategoryColor } from '@/utils/categoryColors';
import { FavoriteButton } from '@/components/FavoriteButton';

export const PhraseCard = ({ phrase, onClick, translatedCategories = [] }) => {
  const { categories } = usePhrases();
  const { isSpanish, t } = useLanguage();
  const { subscriptionStatus } = useSubscription();
  const { user } = useAuth();
  
  // Check if phrase is favorited by user
  const isFavorited = user?.favorite_phrases?.includes(phrase.id) || false;
  
  // Check if user has premium access (includes active, trial, gifted, and admin users)
  const isPremium = subscriptionStatus?.status === 'active' || 
                    subscriptionStatus?.status === 'trial' || 
                    subscriptionStatus?.status === 'admin' ||
                    user?.is_gifted || 
                    user?.is_admin;
  
  // Support multiple categories (from consolidated search results)
  const phraseCategories = phrase.categories || [phrase.category];
  
  // Check if this phrase has multiple unique definitions
  const uniqueMeanings = phrase.allMeanings ? [...new Set(phrase.allMeanings)] : [phrase.actualMeaning];
  const hasMultipleDefinitions = uniqueMeanings.length > 1;
  
  // Get category objects for all categories with proper translation
  const categoryObjects = phraseCategories.map(catId => {
    // First check if we have translated categories from parent
    if (isSpanish && translatedCategories.length > 0) {
      const translated = translatedCategories.find(c => c.id === catId);
      if (translated) return translated;
    }
    // Fall back to categories from context
    const cat = categories.find(c => c.id === catId);
    if (cat) {
      // If Spanish mode and category has nameEs, use it
      if (isSpanish && cat.nameEs) {
        return { ...cat, name: cat.nameEs };
      }
      return cat;
    }
    return null;
  }).filter(Boolean);
  
  // The actualMeaning on cards will be translated when viewing phrase detail
  const actualMeaning = phrase.actualMeaning;
  
  // Display text for the description
  const displayText = hasMultipleDefinitions 
    ? (isSpanish ? 'Múltiples definiciones — ver más' : 'Multiple definitions — view more')
    : actualMeaning;
  
  return (
    <Card
      onClick={onClick}
      className="phrase-card group"
      data-testid="phrase-card"
    >
      <CardHeader>
        <div className="flex items-start justify-between mb-3">
          <div className="flex flex-wrap gap-1 flex-1 min-w-0">
            {categoryObjects.slice(0, 5).map((cat, idx) => {
              const colors = getCategoryColor(cat.id);
              return (
                <span 
                  key={cat.id || idx}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}
                >
                  {cat?.name || 'General'}
                </span>
              );
            })}
            {categoryObjects.length > 5 && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                +{categoryObjects.length - 5} more
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
            <FavoriteButton phraseId={phrase.id} initialFavorited={isFavorited} size="sm" />
            <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
          </div>
        </div>
        <CardTitle className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
          {phrase.phrase}
        </CardTitle>
        <CardDescription 
          className={`text-sm line-clamp-2 leading-relaxed ${hasMultipleDefinitions ? 'italic text-muted-foreground' : ''}`}
        >
          {displayText}
        </CardDescription>
      </CardHeader>
    </Card>
  );
};
